import { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FiCheck, FiMail } from "react-icons/fi";
import { FilterSection, ActiveFilterPills } from "../../components/UserProfiles";
import PaginationBar from "../../components/common/PaginationBar";
import { useAdminAlerts } from "../../hooks/useAdminAlerts";
import {
  alertAccent,
  alertMatchesSearch,
  getAlertChannel,
  sortAlertsNewestFirst,
} from "../../utils/adminAlertsStore";

const LIMIT = 15;

const INITIAL_FILTERS = {
  search: "",
  type: "",
  read_status: "",
  channel: "",
};

const TYPE_OPTIONS = [
  { value: "", name: "All Types" },
  { value: "support_ticket", name: "Support Tickets" },
  { value: "user_registered", name: "New Users" },
  { value: "verification_pending", name: "Verifications" },
  { value: "chat_message", name: "Chat Messages" },
  { value: "user_online", name: "Customer Online" },
];

const READ_OPTIONS = [
  { value: "", name: "All" },
  { value: "unread", name: "Unread" },
  { value: "read", name: "Read" },
];

const CHANNEL_OPTIONS = [
  { value: "", name: "All Channels" },
  { value: "support", name: "Support Chat" },
  { value: "sales", name: "Sales CRM" },
  { value: "csr", name: "CSR Outreach" },
];

const PILL_CLASSES = {
  search: "bg-yellow-100 text-yellow-800",
  type: "bg-rose-100 text-rose-800",
  read_status: "bg-blue-100 text-blue-800",
  channel: "bg-sky-100 text-sky-800",
};

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function getFilterLabel(key, value) {
  if (value == null || value === "") return value;
  if (key === "type") return (TYPE_OPTIONS.find((o) => o.value === value) || {}).name || value;
  if (key === "read_status") return (READ_OPTIONS.find((o) => o.value === value) || {}).name || value;
  if (key === "channel") return (CHANNEL_OPTIONS.find((o) => o.value === value) || {}).name || value;
  return value;
}

export default function AdminAlerts() {
  const navigate = useNavigate();
  const go = useMemo(() => (path) => navigate(path), [navigate]);
  const { alerts, unread, markRead, markUnread, markAllRead } = useAdminAlerts({
    navigate: go,
    enabled: true,
  });

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [applied, setApplied] = useState(INITIAL_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const applyFilters = useCallback(() => {
    setApplied({ ...filters });
    setCurrentPage(1);
  }, [filters]);

  const clearFilters = useCallback(() => {
    setFilters((prev) => ({ ...INITIAL_FILTERS, read_status: prev.read_status }));
    setApplied((prev) => ({ ...INITIAL_FILTERS, read_status: prev.read_status }));
    setCurrentPage(1);
  }, []);

  const removeFilter = useCallback((key) => {
    if (key === "read_status") return;
    setFilters((prev) => ({ ...prev, [key]: "" }));
    setApplied((prev) => ({ ...prev, [key]: "" }));
    setCurrentPage(1);
  }, []);

  const filtered = useMemo(() => {
    return sortAlertsNewestFirst(
      alerts.filter((alert) => {
        if (!alertMatchesSearch(alert, applied.search)) return false;
        if (applied.type && alert.type !== applied.type) return false;
        if (applied.read_status === "unread" && alert.read) return false;
        if (applied.read_status === "read" && !alert.read) return false;
        if (applied.channel) {
          if (alert.type !== "chat_message") return false;
          if (getAlertChannel(alert) !== applied.channel) return false;
        }
        return true;
      })
    );
  }, [alerts, applied]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / LIMIT));
  const page = Math.min(currentPage, totalPages);
  const pageItems = useMemo(() => {
    const start = (page - 1) * LIMIT;
    return filtered.slice(start, start + LIMIT);
  }, [filtered, page]);

  const activeFilterEntries = useMemo(
    () =>
      Object.keys(applied).filter(
        (k) => k !== "read_status" && applied[k] !== "" && applied[k] != null
      ),
    [applied]
  );

  const primaryFields = useMemo(
    () => [
      {
        name: "search",
        label: "Search",
        type: "text",
        placeholder: "Name, email, phone, ticket ID, message…",
      },
      {
        name: "type",
        label: "Type",
        type: "select",
        options: TYPE_OPTIONS,
      },
      {
        name: "channel",
        label: "Chat Channel",
        type: "select",
        options: CHANNEL_OPTIONS,
      },
    ],
    []
  );

  const setReadTab = useCallback((value) => {
    const next = value === "all" ? "" : value;
    setFilters((prev) => ({ ...prev, read_status: next }));
    setApplied((prev) => ({ ...prev, read_status: next }));
    setCurrentPage(1);
  }, []);

  const openAlert = (alert) => {
    markRead(alert.id);
    if (alert.route) navigate(alert.route);
  };

  return (
    <div className="p-4 w-full">
      <div className="bg-white rounded-xl shadow-xl p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
          <div>
            <h2 className="text-md md:text-2xl font-bold text-gray-800">My Notifications</h2>
            <p className="text-xs text-gray-500 mt-1">
              Your personal inbox. Clearing the header bell does not remove alerts here.
              {unread > 0 ? (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-100 text-rose-700">
                  {unread} unread
                </span>
              ) : null}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {activeFilterEntries.length > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs sm:text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Clear All Filters
              </button>
            )}
            {alerts.length > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                disabled={unread <= 0}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white"
              >
                <FiCheck className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {[
            { id: "", label: "All" },
            { id: "unread", label: unread ? `Unread (${unread})` : "Unread" },
            { id: "read", label: "Read" },
          ].map((tab) => {
            const active = (applied.read_status || "") === tab.id;
            return (
              <button
                key={tab.id || "all"}
                type="button"
                onClick={() => setReadTab(tab.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                  active
                    ? "bg-[#FF2D38] text-white shadow-sm"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <ActiveFilterPills
          activeKeys={activeFilterEntries}
          filters={applied}
          getLabel={getFilterLabel}
          onRemove={removeFilter}
          label="Active Filters:"
          getPillClassName={(key) => PILL_CLASSES[key] || "bg-gray-100 text-gray-800"}
        />

        <FilterSection
          primaryFields={primaryFields}
          secondaryFields={[]}
          values={filters}
          onChange={(e) => handleFilterChange(e.target.name, e.target.value)}
          onApply={applyFilters}
          onClear={clearFilters}
          applyLabel="Apply"
          clearLabel="Clear All"
        />

        <div className="overflow-x-auto rounded-lg border border-gray-200 mt-2">
          <table className="min-w-full divide-y divide-gray-200 bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Type
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Notification
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Details
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  When
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Status
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pageItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">
                    {alerts.length === 0
                      ? "No admin alerts yet. New support tickets, chats, signups and verifications will appear here."
                      : "No alerts match your filters."}
                  </td>
                </tr>
              ) : (
                pageItems.map((alert) => {
                  const accent = alertAccent(alert.type);
                  const channel = getAlertChannel(alert);
                  const meta = alert.meta || {};
                  return (
                    <tr
                      key={alert.id}
                      className={`transition-colors ${
                        alert.read ? "bg-white hover:bg-gray-50" : "bg-rose-50/40 hover:bg-rose-50/70"
                      }`}
                    >
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${accent.badge}`}
                        >
                          <span>{accent.icon}</span>
                          {accent.label}
                        </span>
                        {channel ? (
                          <div className="text-[10px] text-gray-400 mt-1 capitalize">{channel}</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 max-w-[280px]">
                        <div className="text-sm font-medium text-gray-900">{alert.title}</div>
                        {alert.message ? (
                          <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{alert.message}</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-600">
                        {meta.displayName || meta.fromName || meta.name || meta.subjectName ? (
                          <div>
                            {meta.displayName || meta.fromName || meta.name || meta.subjectName}
                          </div>
                        ) : null}
                        {meta.email ? <div className="text-gray-400">{meta.email}</div> : null}
                        {meta.phone ? <div className="text-gray-400">{meta.phone}</div> : null}
                        {meta.ticketId ? <div className="text-gray-400">Ticket #{meta.ticketId}</div> : null}
                        {meta.userId ? <div className="text-gray-400">User #{meta.userId}</div> : null}
                        {meta.chatId ? <div className="text-gray-400">Chat #{meta.chatId}</div> : null}
                        {!meta.displayName &&
                        !meta.fromName &&
                        !meta.name &&
                        !meta.email &&
                        !meta.ticketId &&
                        !meta.userId ? (
                          <span className="text-gray-400">—</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-600">
                        <div>{timeAgo(alert.receivedAt || alert.at)}</div>
                        <div className="text-gray-400">{formatDateTime(alert.receivedAt || alert.at)}</div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {alert.read ? (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">
                            Read
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-100 text-rose-700">
                            Unread
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex flex-col items-start gap-1.5">
                          <button
                            type="button"
                            onClick={() => openAlert(alert)}
                            className="text-xs font-semibold text-[#FF2D38] hover:underline"
                          >
                            Open →
                          </button>
                          {alert.read ? (
                            <button
                              type="button"
                              onClick={() => markUnread(alert.id)}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 hover:text-[#FF2D38]"
                            >
                              <FiMail className="h-3 w-3" />
                              Mark unread
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => markRead(alert.id)}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 hover:text-[#FF2D38]"
                            >
                              <FiCheck className="h-3 w-3" />
                              Mark read
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <PaginationBar
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={LIMIT}
          label="alerts"
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
}
